import { cn } from "@/lib/utils";
import { STRINGS } from "@/i18n";
import type { ProviderFilterId } from "@/utils/providerConditions";

interface ProviderFiltersProps {
  /** Filters that can change the current list, in display order. */
  available: ProviderFilterId[];
  active: ReadonlySet<ProviderFilterId>;
  onToggle: (filter: ProviderFilterId) => void;
  onClear: () => void;
  shownCount: number;
  totalCount: number;
}

/**
 * Toggle buttons that narrow the providers list; filters combine as AND. On phones the
 * buttons sit in one horizontally scrollable row, from the tablet width they wrap.
 */
export function ProviderFilters({
  available,
  active,
  onToggle,
  onClear,
  shownCount,
  totalCount,
}: ProviderFiltersProps) {
  const t = STRINGS.providers;
  if (available.length === 0) return null;
  const activeCount = available.filter((filter) => active.has(filter)).length;

  return (
    <div className="border-b border-border px-4 py-3 md:px-6">
      <div
        role="group"
        aria-label={t.filterTitle}
        className="-mx-4 flex items-center gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] md:mx-0 md:flex-wrap md:overflow-visible md:px-0 md:pb-0"
      >
        <span className="mr-1 shrink-0 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {t.filterTitle}
        </span>
        {available.map((filter) => {
          const isActive = active.has(filter);
          return (
            <button
              key={filter}
              type="button"
              aria-pressed={isActive}
              onClick={() => onToggle(filter)}
              className={cn(
                "min-h-9 shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
                isActive
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-foreground hover:bg-muted"
              )}
            >
              {t.filters[filter]}
            </button>
          );
        })}
      </div>
      {activeCount > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {t.filterCount(shownCount, totalCount)} ·{" "}
          <button
            type="button"
            onClick={onClear}
            className="underline underline-offset-2 hover:no-underline"
          >
            {t.clearFilters}
          </button>
        </p>
      )}
    </div>
  );
}
